import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import type { Facets } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useQuery } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { useFacets } from '@jane/dm/internal';
import { useGetStoreSpecialsPage } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { BannerMessageSetting, Store } from '@jane/shared/types';

import { storeBannerMessageSettingsQueryBody } from '../../data-access/lib/getStoreBannerMessageSettings/getStoreBannerMessageSettings';
import type { RootLoaderType } from '../../data-access/lib/loaders/rootLoader';
import { useTypedRouteLoaderData } from '../../hooks/lib/useTypedRouteLoaderData';
import type { MenuCategory } from '../../types';
import {
  buildRootTypeFilter,
  isSupportedStoreSpecial,
  visibleTabs,
} from '../../util';
import { useBrandSpecials } from './BrandSpecialsProvider/BrandSpecialsProvider';
import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';

interface MenuContext {
  bannerMessage?: BannerMessageSetting | null;
  categoryFacets?: Facets;
  filterableSpecialIds: string[];
  isDataLoaded: boolean;
  visibleTabs: MenuCategory[];
}

const MenuContext = createContext<MenuContext | null>(null);

export const MenuProvider = ({ children }: PropsWithChildren) => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { category } = useParams<{
    category: MenuCategory;
  }>();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);

  const { data: store, isSuccess: isStoreSuccess } =
    useGetStore<Store>(storeId);
  const storewideFacetsQuery = useFacets({
    storeId,
  });
  const { facets: storewideFacets } = storewideFacetsQuery;

  const filterableSpecialIds = useMemo(
    () => Object.keys(storewideFacets?.['applicable_special_ids'] ?? {}),
    [storewideFacets]
  );

  const categoryFacetsQuery = useFacets({
    storeId,
    searchFilter: buildRootTypeFilter([
      category === 'specials' ? 'sale' : category,
    ]),
  });

  const { brandSpecials } = useBrandSpecials();

  const v1SpecialsQuery = useGetStoreSpecialsPage(storeId, {
    enabled: !useV3 && !!storeId,
  });

  const v3SpecialsQuery = useStoreSpecials({
    appMode: 'kiosk',
    enabled: !!useV3 && !!storeId,
    storeId,
  });

  const isSpecialsSuccess = useV3
    ? v3SpecialsQuery.isSuccess
    : v1SpecialsQuery.isSuccess;

  const supportedStoreSpecials = useMemo(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return (
      v1SpecialsQuery.data?.pages
        .flatMap((page) => page.specials)
        .filter((special) => isSupportedStoreSpecial(special)) ?? []
    );
  }, [useV3, v3SpecialsQuery.data, v1SpecialsQuery.data]);

  const tabs = visibleTabs({
    facets: storewideFacets,
    store,
    hasBrandSpecials: !!brandSpecials?.length,
    hasSpecials: !!supportedStoreSpecials.length,
    // v3 backend filters out hidden specials server-side (see Special.not_hidden),
    // so any returned special has both hidden flags false. Drop this branch and
    // the v1 fallback once c3UseV3StoreSpecials is fully rolled out.
    allSpecialsHidden: useV3
      ? false
      : supportedStoreSpecials.every(
          (special) => special.hidden_from_page && special.hidden_from_row
        ),
  });

  const { data: bannerMessage } = useQuery(
    storeBannerMessageSettingsQueryBody(storeId)
  );

  const contextValue = useMemo<MenuContext>(
    () => ({
      bannerMessage,
      categoryFacets: categoryFacetsQuery.facets,
      filterableSpecialIds,
      isDataLoaded:
        isStoreSuccess &&
        isSpecialsSuccess &&
        storewideFacetsQuery.isSuccess &&
        categoryFacetsQuery.isSuccess,
      visibleTabs: tabs,
    }),
    [
      bannerMessage,
      categoryFacetsQuery,
      filterableSpecialIds,
      isSpecialsSuccess,
      isStoreSuccess,
      storewideFacetsQuery,
      tabs,
    ]
  );

  return (
    <MenuContext.Provider value={contextValue}>{children}</MenuContext.Provider>
  );
};

export const useMenu = () =>
  useEnsureContextWithinProvider(MenuContext, 'MenuContext');
